import { ConfigDefinition, Config, Context, facetRef, layoutRef, SnippetTemplate } from '../types/catalog';
import { URLManagerSearch } from '../services/url-manager/url-manager-search';
import * as URLManagerCategory from '../services/url-manager/url-manager-category';
import { FeatureFlag, isEnabled } from '@monolith/legacy/services/features';
import type { ProductSortBy } from '../types/product-search-parameters';

import { getLang } from '@core/utilities/lang';

export const config: Record<Context, ConfigDefinition> = {
  [Context.NEW_PRODUCTS]: {
    template: SnippetTemplate.PRODUCTS,
    name: Context.NEW_PRODUCTS,
    layout: [
      layoutRef.withBreadCrumb,
      layoutRef.withTitle,
      layoutRef.withTabs,
      layoutRef.withFilterSidebar,
      layoutRef.withQuickFilters,
      layoutRef.withCategoriesFilter,
      layoutRef.withSimilarProductsDrawer,
    ],
    eventTile: true,
    filters: [
      facetRef.category,
      facetRef.locationBrand,
      facetRef.made_in,
      facetRef.tags,
      facetRef.margin,
      facetRef.retail_price,
      facetRef.shipping_lead_time,
      facetRef.discount,
      facetRef.brand,
      facetRef.product_type,
    ],
    quickFilters: [facetRef.tags, facetRef.margin, facetRef.discount, facetRef.aks_systems],
    urlManager: URLManagerCategory,
    maxPages: 5,
  },
  [Context.NEW_BRANDS]: {
    template: SnippetTemplate.BRANDS,
    name: Context.NEW_BRANDS,
    layout: [
      layoutRef.withBreadCrumb,
      layoutRef.withTitle,
      isEnabled('RET-2902') ? layoutRef.withTabs : undefined,
      layoutRef.withFilterSidebar,
      layoutRef.withQuickFilters,
      layoutRef.withCategoriesFilter,
    ],
    eventTile: true,
    filters: [
      facetRef.category,
      facetRef.location,
      facetRef.made_in,
      facetRef.tags,
      facetRef.marginBrand,
      facetRef.shipping_lead_timeBrand,
      facetRef.discount,
    ],
    quickFilters: [facetRef.tags, facetRef.marginBrand, facetRef.discount],
    urlManager: URLManagerCategory,
    maxPages: 5,
  },
  [Context.SEARCH]: {
    template: SnippetTemplate.PRODUCTS,
    name: Context.SEARCH,
    layout: [
      layoutRef.withTitle,
      layoutRef.withCategoriesFilter,
      layoutRef.withBrandSuggestion,
      layoutRef.withCategoryTiles,
      layoutRef.withFilterSidebar,
      layoutRef.withQuickFilters,
      layoutRef.withSimilarProductsDrawer,
    ],
    eventTile: false,
    filters: [
      facetRef.category,
      facetRef.locationBrand,
      facetRef.made_in,
      facetRef.brand,
      facetRef.tags,
      facetRef.margin,
      facetRef.retail_price,
      facetRef.shipping_lead_time,
      facetRef.discount,
      facetRef.product_type,
    ],
    quickFilters: [facetRef.tags, facetRef.margin, facetRef.discount, facetRef.aks_systems],
    urlManager: URLManagerSearch,
  },
  [Context.CATEGORY_PRODUCTS]: {
    name: Context.CATEGORY_PRODUCTS,
    template: SnippetTemplate.PRODUCTS,
    eventTile: true,
    layout: [
      layoutRef.withBreadCrumb,
      layoutRef.withTitle,
      layoutRef.withTabs,
      layoutRef.withFilterSidebar,
      layoutRef.withQuickFilters,
      layoutRef.withCategoriesFilter,
      layoutRef.withSort,
      layoutRef.withSimilarProductsDrawer,
    ],
    filters: [
      facetRef.category,
      facetRef.locationBrand,
      facetRef.made_in,
      facetRef.tags,
      facetRef.margin,
      facetRef.retail_price,
      facetRef.shipping_lead_time,
      facetRef.discount,
      facetRef.brand,
      facetRef.product_type,
    ],
    quickFilters: [facetRef.tags, facetRef.margin, facetRef.discount, facetRef.aks_systems],
    urlManager: URLManagerCategory,
  },
  [Context.CATEGORY_BRANDS]: {
    template: SnippetTemplate.BRANDS,
    name: Context.CATEGORY_BRANDS,
    layout: [
      layoutRef.withBreadCrumb,
      layoutRef.withTitle,
      layoutRef.withTabs,
      layoutRef.withFilterSidebar,
      layoutRef.withQuickFilters,
      layoutRef.withCategoriesFilter,
      layoutRef.withSort,
    ],
    eventTile: true,
    filters: [
      facetRef.category,
      facetRef.location,
      facetRef.made_in,
      facetRef.tags,
      facetRef.marginBrand,
      facetRef.shipping_lead_timeBrand,
      facetRef.discount,
    ],
    quickFilters: [facetRef.tags, facetRef.marginBrand, facetRef.discount],
    urlManager: URLManagerCategory,
  },
  [Context.BRAND]: {
    name: Context.BRAND,
    template: SnippetTemplate.PRODUCTS,
    layout: [
      layoutRef.withSearchInput,
      layoutRef.withFilterSidebar,
      layoutRef.withQuickFilters,
      layoutRef.withCategoriesFilter,
      layoutRef.withSort,
    ],
    eventTile: false,
    filters: [
      facetRef.category,
      facetRef.discount,
      facetRef.made_in,
      facetRef.tags,
      facetRef.margin,
      facetRef.retail_price,
      facetRef.shipping_lead_time,
      facetRef.product_type,
    ],
    quickFilters: [facetRef.discount, facetRef.tags, facetRef.margin, facetRef.retail_price],
    urlManager: URLManagerSearch,
  },
  [Context.COLLECTION_PRODUCTS]: {
    name: Context.COLLECTION_PRODUCTS,
    template: SnippetTemplate.PRODUCTS,
    layout: [layoutRef.withSearchInput, layoutRef.withFilterSidebar, layoutRef.withQuickFilters, layoutRef.withCategoriesFilter],
    eventTile: true,
    filters: [
      facetRef.category,
      facetRef.made_in,
      facetRef.tags,
      facetRef.margin,
      facetRef.retail_price,
      facetRef.shipping_lead_time,
      facetRef.discount,
      facetRef.product_type,
    ],
    quickFilters: [facetRef.tags, facetRef.margin, facetRef.retail_price, facetRef.discount, facetRef.aks_systems],
    urlManager: URLManagerSearch,
  },
  [Context.COLLECTION_BRANDS]: {
    template: SnippetTemplate.BRANDS,
    name: Context.COLLECTION_BRANDS,
    layout: [layoutRef.withSearchInput, layoutRef.withFilterSidebar, layoutRef.withQuickFilters, layoutRef.withCategoriesFilter],
    eventTile: true,
    filters: [
      facetRef.category,
      facetRef.location,
      facetRef.made_in,
      facetRef.tags,
      facetRef.marginBrand,
      facetRef.shipping_lead_timeBrand,
      facetRef.discount,
    ],
    quickFilters: [facetRef.tags, facetRef.marginBrand, facetRef.discount],
    urlManager: URLManagerSearch,
  },
  [Context.WHOLESALE]: {
    template: SnippetTemplate.PRODUCTS,
    name: Context.WHOLESALE,
    layout: [layoutRef.withBreadCrumb, layoutRef.withTitle, layoutRef.withFilterSidebar],
    eventTile: true,
    filters: [],
    quickFilters: [],
    urlManager: URLManagerSearch,
    maxPages: 10,
  },
};

// Read per call rather than baked into the layout array above, so the kill switch takes effect without
// a full page load and stays testable.
// CATEGORY_BRANDS is off unconditionally: a single score exists for brands, the other sort fields are unmapped.
const isSortDisabled = (context: Context): boolean =>
  context === Context.CATEGORY_BRANDS || (context === Context.BRAND && isEnabled(FeatureFlag.DisableBrandPageSortBy));

/**
 * Which sorts each surface offers, values only: the labels are presentation and stay in the component.
 *
 * This is the single source of truth for the sort contract, so the menu, the `sortBy` URL param and the
 * kill switch cannot disagree. A value absent from a context's list is not offered there AND is ignored
 * when it arrives on the URL, which is what makes a stale or cross-context link inert rather than
 * silently reordering a catalog with a sort the retailer can neither see nor undo.
 *
 * `best_of` is the "no sortBy param" sentinel, so its presence means "this surface has a default option"
 * rather than a sort key the backend ever receives.
 */
const BRAND_SORT_OPTIONS: ProductSortBy[] = ['best_of', 'popularity:desc', 'latest:desc'];

const SHARED_SORT_OPTIONS: ProductSortBy[] = ['best_of', 'mostordered:desc', 'latest:desc', 'trending:desc'];

export function getSortOptions(context = Context.SEARCH): ProductSortBy[] {
  // Empty rather than the brand set once the kill switch is on, which is what makes a sortBy the switch
  // just withdrew inert instead of merely invisible. Deliberately scoped to the brand page: every other
  // surface keeps honouring its existing sortBy deep links.
  if (isSortDisabled(context)) {
    return [];
  }

  if (context === Context.BRAND) {
    return BRAND_SORT_OPTIONS;
  }

  return isEnabled('disable_PRICE-5_max_discount_rate_sort')
    ? SHARED_SORT_OPTIONS
    : [...SHARED_SORT_OPTIONS, 'max_discount_rate:desc'];
}

/** The `sortBy` URL param, or undefined when this surface does not offer it. */
export function resolveSortBy(context: Context, sortBy: string | undefined): ProductSortBy | undefined {
  const options = getSortOptions(context);

  return options.includes(sortBy as ProductSortBy) ? (sortBy as ProductSortBy) : undefined;
}

export function getCatalogConfig(context = Context.SEARCH): Config {
  return {
    context,
    template: config[context].template,
    withTabs: config[context].layout.includes(layoutRef.withTabs),
    withBreadCrumb: config[context].layout.includes(layoutRef.withBreadCrumb),
    withTitle: config[context].layout.includes(layoutRef.withTitle),
    withSearchInput: config[context].layout.includes(layoutRef.withSearchInput),
    withCategoriesFilter: config[context].layout.includes(layoutRef.withCategoriesFilter),
    withSort: config[context].layout.includes(layoutRef.withSort) && !isSortDisabled(context),
    withBrandSuggestion: config[context].layout.includes(layoutRef.withBrandSuggestion),
    withSimilarProductsDrawer: config[context].layout.includes(layoutRef.withSimilarProductsDrawer),
    withCategoryTiles: config[context].layout.includes(layoutRef.withCategoryTiles),
    withFilterSidebar: config[context].layout.includes(layoutRef.withFilterSidebar),
    withQuickFilters: config[context].layout.includes(layoutRef.withQuickFilters),
    Facets: [],
    quickFilters: [],
    urlManager: config[context].urlManager,
    eventTile: config[context].eventTile ?? false,
    maxPages: config[context].maxPages,
  };
}

export function getRestrictedSearchableAttributes(context = Context.SEARCH) {
  if (context === Context.NEW_BRANDS) {
    return [];
  }
  return [`searchable_name.${getLang()}`, `description.${getLang()}`, 'brand.name', 'brand.synonyms', 'options.sku'];
}

export function getAttributeToRetrieve(template = SnippetTemplate.PRODUCTS) {
  if (template === SnippetTemplate.BRANDS) {
    return [
      'id',
      'uuid',
      'active',
      'business_events_eligible',
      'business_events',
      'link',
      'images.squared',
      'images.large',
      'images.rounded',
      'name',
      'active',
      'featured_products',
      'minimum',
      'opened_countries',
      'has_new_products',
      'shipping_lead_time',
      'inserted_at',
      'brand_tags',
      'discount',
      'available_at',
    ];
  }
  return [
    'id',
    'name',
    `description.${getLang()}`,
    `tags.${getLang()}`,
    'brand',
    'retail_price',
    'wholesale_price',
    'original_wholesale_price',
    'discount_rate',
    'unit_multiplier',
    'link',
    'options',
    'images',
    'is_new',
    'out_of_stock',
    'discount_event_price',
    'margin',
    'variants',
    'display_name',
    'uuid',
    'available_at',
    'aks_systems',
    'product_type'
  ];
}

const addCategoryFilter = (filter: string, category: number) => {
  if (!category) {
    return filter;
  }

  const categoryFilter = `catalog_categories.v3:${category}`;

  return filter ? `${filter} AND (${categoryFilter})` : categoryFilter;
};

export function getDefaultFilter(
  params: {
    country: string;
    brandId?: number;
    user?: { business: { brand?: { id: number } } };
    categoryId?;
    isStaff?: boolean;
    sortBy?: string;
  },
  context = Context.SEARCH,
  customFilter = ''
) {
  let filters = '';
  switch (context) {
    case Context.NEW_PRODUCTS:
      filters = `brand.opened_countries:"${params.country}" AND is_new:true AND sorts=[latest:desc]`;
      break;
    case Context.NEW_BRANDS:
      filters = isEnabled('RET-2902')
        ? `opened_countries:"${params.country}" AND is_new:true AND active = 1`
        : `opened_countries:"${params.country}" AND active = 1`;
      break;
    case Context.SEARCH:
      filters = `(brand.opened_countries:"${params.country}")`;
      break;
    case Context.CATEGORY_PRODUCTS:
      filters = `(brand.opened_countries:"${params.country}")`;
      filters = addCategoryFilter(filters, params.categoryId);
      break;
    case Context.COLLECTION_PRODUCTS:
      filters = `(brand.opened_countries:"${params.country}")`;
      break;
    case Context.CATEGORY_BRANDS:
      filters = `(opened_countries:"${params.country}")`;
      filters = addCategoryFilter(filters, params.categoryId);
      break;
    case Context.COLLECTION_BRANDS:
      filters = `(opened_countries:"${params.country}")`;
      break;
    case Context.BRAND:
      if (params.user?.business.brand?.id === params.brandId || params.isStaff) {
        // Allow brands to view their own page without restrictions when using the "See my store" feature
        filters = `(brand.id=${params.brandId})`;
      } else {
        filters = `(brand.id=${params.brandId}) AND (brand.opened_countries:"${params.country}")`;
      }
      break;
  }
  if (params.sortBy) {
    filters = `${filters} AND (sorts=[${params.sortBy}])`;
  }
  if (customFilter) {
    filters = `${filters} AND (${customFilter})`;
  }
  return filters;
}
